import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analysisKeys } from './queryKeys';
import type { AnalysisProfile } from './types';

interface UseAnalysisProfileQueryArgs {
  projectId: string;
  analysisProfileId: string;
}

// Poll cadence while a run is in flight. Mirrors the analytical-dataset
// profile poll (useAnalyticalDatasetProfileQuery) — a worker flips
// latestStatus PROCESSING -> PROCESSED/ERROR out of band, and the profile GET
// is the signal the UI watches.
const PROCESSING_POLL_INTERVAL_MS = 5000;

// Keyed on `profileId` only — same cache entry across versions.
export const useAnalysisProfileQuery = ({
  projectId,
  analysisProfileId,
}: UseAnalysisProfileQueryArgs) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: analysisKeys.profile(analysisProfileId),
    queryFn: () =>
      authFetch<ObjectResponse<AnalysisProfile>>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}`
      ),
    enabled: projectId.length > 0 && analysisProfileId.length > 0,
    retry: false,
    // Auto-poll only while the latest run is PROCESSING; stop on any terminal
    // status. No external `shouldPoll` flag needed — the cached status drives it.
    refetchInterval: (query) =>
      query.state.data?.data.latestStatus === 'PROCESSING' ? PROCESSING_POLL_INTERVAL_MS : false,
    // Recover a stranded PROCESSING status if the tab slept through the worker
    // finishing (matches the analytical-dataset profile query's behavior).
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  // The results table and the per-patient dataset grid are separate queries; a
  // finished run changes their cells/rows without touching their caches, so
  // refetch them when the status leaves PROCESSING. The dataset grid's keys carry
  // the dataset profile id and grid params, so it is invalidated by prefix.
  // Every component mounting this hook runs this effect on the same transition;
  // `cancelRefetch: false` joins the in-flight request instead of cancelling and
  // restarting it once per mounted hook.
  const latestStatus = query.data?.data.latestStatus;
  const previousStatus = useRef(latestStatus);
  useEffect(() => {
    if (previousStatus.current === 'PROCESSING' && latestStatus !== 'PROCESSING') {
      queryClient.invalidateQueries(
        { queryKey: analysisKeys.results(analysisProfileId) },
        { cancelRefetch: false }
      );
      queryClient.invalidateQueries(
        { queryKey: analysisKeys.datasetResultsAll() },
        { cancelRefetch: false }
      );
    }
    previousStatus.current = latestStatus;
  }, [latestStatus, analysisProfileId, queryClient]);

  return query;
};
